import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  hasError?: boolean;
}

const baseCls = (hasError?: boolean) =>
  cn(
    "h-11 w-full rounded-lg border bg-bg-subtle/60 px-3.5 text-sm text-fg placeholder:text-fg-subtle outline-none transition-colors",
    "focus:border-primary/50 focus:bg-bg-elevated focus:ring-2 focus:ring-primary/30",
    hasError ? "border-danger/50" : "border-border",
    "disabled:cursor-not-allowed disabled:opacity-60",
  );

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, hasError, type = "text", ...props }, ref) => {
    const [show, setShow] = React.useState(false);

    // Password fields get a built-in show/hide (eye) toggle.
    if (type === "password") {
      return (
        <div className="relative">
          <input
            type={show ? "text" : "password"}
            ref={ref}
            className={cn(baseCls(hasError), "pr-10", className)}
            {...props}
          />
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShow((s) => !s)}
            aria-label={show ? "Hide password" : "Show password"}
            title={show ? "Hide password" : "Show password"}
            className="absolute right-2 top-1/2 -translate-y-1/2 grid h-7 w-7 place-items-center rounded-md text-fg-subtle transition-colors hover:text-fg hover:bg-bg-elevated/60"
          >
            {show ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
          </button>
        </div>
      );
    }

    return (
      <input
        type={type}
        ref={ref}
        className={cn(baseCls(hasError), className)}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export const Label = React.forwardRef<
  HTMLLabelElement,
  React.LabelHTMLAttributes<HTMLLabelElement>
>(({ className, ...props }, ref) => (
  <label
    ref={ref}
    className={cn("text-xs font-medium text-fg-muted uppercase tracking-wide", className)}
    {...props}
  />
));
Label.displayName = "Label";
